'use client';

/**
 * Full-height podcast section — a centred intro (eyebrow, headline,
 * description, outline pill buttons), a row of listening-platform badges
 * and a rail of episode cards with prev/next arrows and position dots,
 * over a dark background lit by two authorable glows.
 *
 * <pre>
 *   ┌──────────────────────────────────────────────────────────┐
 *   │                BTK Akademi Podcastleri ile               │
 *   │                 Bilginin Sesine Kulak Ver          ░glow░│
 *   │                   Two-line description                   │
 *   │          ( Tüm Bölümleri Keşfet ) ( Dinleme Platf. )     │
 *   │             [Spotify]  [Apple Podcasts]  [Music]         │
 *   │                                                          │
 *   │   ‹   ┌────┐     ┌────┐     ┌────┐     ┌────┐   ›        │
 *   │       │YENİ│     │    │     │    │     │    │            │
 *   │       │▶21 │     │▶15 │     │▶13 │     │▶09 │            │
 *   │       └────┘     └────┘     └────┘     └────┘            │
 *   │ ░glow░ Title     Title      Title      Title             │
 *   │                   ▬ • • • • •                            │
 *   └──────────────────────────────────────────────────────────┘
 * </pre>
 *
 * <p>Type follows the design: Inter by default (authorable, falling back to
 * the theme font), 18px Regular #B1B1B1 eyebrow, 40px Medium headline,
 * 18px Regular description; on the cards a 15px Regular title, 10px
 * Regular #B1B1B1 copy, a 10px Medium #A7A7A7 duration pill and a 10px
 * Bold "YENİ" badge.
 *
 * <p>The rail is a native horizontal scroller with snap points, so touch
 * swipe, trackpads and the keyboard all work without a gesture handler;
 * the arrows (wrap-around) and dots only drive its scroll position. How
 * many cards fit is decided in CSS by container queries — four on a wide
 * screen down to two on a phone — so nothing jumps at hydration; the
 * script only measures that to know how many dots there are.
 *
 * <p>Episode cards have two sources, same as the {@code Podcasts} block:
 * <ul>
 *   <li><b>Manual</b> ({@code source: 'authored'}, the default) — renders
 *       the {@code episodes} array.</li>
 *   <li><b>Automatic</b> ({@code source: 'catalog'}) — podcast courses from
 *       the LMS catalog, through the feed {@code Podcasts} uses
 *       ({@link usePodcastCourses}). The authored episodes are never a
 *       stand-in: until the catalog answers, and when it has no podcasts,
 *       the intro shows without the rail.</li>
 * </ul>
 *
 * <p>Full height comes from {@link FullHeightSlider} (as a single slide),
 * and the glows are its backdrop.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@akademi/ui';
import {
  COURSE_DETAIL_BASE,
  PortalLink,
  courseDetailHref,
  isVisible,
  useBlockPart,
  useCurrentUser,
  type CurrentUser,
  type LearningItem,
} from '@akademi/site-builder-runtime';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { asList, tx } from '../_shared/coerce';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { FullHeightFrame, FullHeightSlider } from '../_shared/FullHeightSlider';
import { PODCAST_PLUGIN_ID, usePodcastCourses, type PodcastSource } from './podcastCatalog';

type Localized = string | Record<string, string>;

export interface FullHeightPodcastButton {
  label?: Localized;
  href?: string;
  newTab?: boolean;
}

export type FullHeightPodcastPlatformKind = 'spotify' | 'apple' | 'youtube-music' | 'custom';

export interface FullHeightPodcastPlatform {
  /** Built-in badge artwork; `custom` draws `logo` and/or `label`. */
  platform?: FullHeightPodcastPlatformKind;
  /** Replaces the platform's name on the badge (and is its accessible name). */
  label?: Localized;
  /** Logo image for a `custom` platform. */
  logo?: string;
  href?: string;
  newTab?: boolean;
}

export interface FullHeightPodcastEpisode extends CampaignAudience {
  title?: Localized;
  description?: Localized;
  /** Cover art, cropped to the 208×218 card. */
  image?: string;
  /** Leave empty when the title already says what the cover shows. */
  imageAlt?: string;
  /** Shown in the pill on the cover, e.g. "21 dk". */
  duration?: Localized;
  /** Red label in the cover's corner, e.g. "YENİ". Empty = none. */
  badge?: Localized;
  href?: string;
  newTab?: boolean;
}

export interface FullHeightPodcastProps {
  eyebrow?: Localized;
  title?: Localized;
  description?: Localized;
  buttons?: FullHeightPodcastButton[];
  platforms?: FullHeightPodcastPlatform[];
  /** {@code authored} (default) renders {@code episodes}; {@code catalog}
   *  pulls podcast courses from the LMS, like the Podcasts block. */
  source?: PodcastSource;
  /** Catalog mode: course-plugin id the search is filtered by. */
  pluginId?: string;
  /** Catalog mode: how many podcasts to pull. */
  limit?: number;
  /** Catalog mode: {@code searchCourses} sort ({@code recent} |
   *  {@code published} | {@code popular} | {@code viewed}). */
  sortField?: string;
  /** Catalog mode: base path for a card's link. */
  hrefBase?: string;
  /** Manual mode's cards. */
  episodes?: FullHeightPodcastEpisode[];
  titleTag?: 'h1' | 'h2';
  /** CSS font family for all text. Empty = the theme's font. */
  fontFamily?: string;

  backgroundColor?: string;
  showGlow?: boolean;
  /** Main glow (right of centre in the design). */
  glowColor?: string;
  glowX?: number;
  glowY?: number;
  showSecondaryGlow?: boolean;
  /** Second glow (bottom left in the design). */
  secondaryGlowColor?: string;
  secondaryGlowX?: number;
  secondaryGlowY?: number;
  /** Both glows' strength, 0–100 (%). */
  glowIntensity?: number;
  /** Both glows' reach, as % of the designed size. */
  glowSize?: number;
}

// Sizes, breakpoints and the rail geometry live here — inline styles can't
// express them. Rail: `--fhp-per` cards of at most 208px sit in a row no
// wider than the design's (120px gaps); when the container is narrower the
// gaps close to 16px and then the cards shrink. `cqw` resolves against the
// `.fhp-slider` container, so the arrows travel inward with the rail. The
// vh lines are the fallback for engines without dvh.
const FULL_HEIGHT_PODCAST_CSS = `
.fh-slider-frame.fhp-frame { padding-inline: 24px; }
.fhp-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1384px;
  margin: 0 auto;
  text-align: center;
}
.fhp-inner > :first-child { margin-top: 0 !important; }
.fhp-eyebrow { margin: 0; font-size: 18px; font-weight: 400; line-height: 22px; color: #B1B1B1; }
.fhp-title { margin: 21px 0 0; font-size: 40px; font-weight: 500; line-height: 1.21; color: #ffffff; }
.fhp-desc { max-width: 830px; margin: 35px 0 0; font-size: 18px; font-weight: 400; line-height: 22px; color: #ffffff; }
.fhp-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px 26px;
  margin-top: clamp(32px, 6.2vh, 67px);
  margin-top: clamp(32px, 6.2dvh, 67px);
}
.fhp-btn { background-color: transparent; color: #ffffff; }
.fhp-btn:hover { background-color: rgba(255, 255, 255, 0.12); color: #ffffff; }
.fhp-btn:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.85); outline-offset: 2px; }
.fhp-platforms {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px 59px;
  /* Full width: a shrink-to-fit row sizes to the badges' content, not
     their 132px basis, and would wrap the last one. */
  width: 100%;
  margin: clamp(28px, 6.7vh, 72px) 0 0;
  margin-top: clamp(28px, 6.7dvh, 72px);
  padding: 0;
  list-style: none;
}
.fhp-platforms > li { flex: 0 1 132px; min-width: 0; }
.fhp-platform {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  padding: 0 10px;
  overflow: hidden;
  background: #FFFFFF 0% 0% no-repeat padding-box;
  border: 1px solid #F2F4F7;
  border-radius: 10px;
  text-decoration: none;
  transition: transform 150ms ease, box-shadow 150ms ease;
}
a.fhp-platform:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28); }
a.fhp-platform:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.85); outline-offset: 2px; }
.fhp-slider {
  container: fhp / inline-size;
  width: 100%;
  margin-top: clamp(40px, 9.8vh, 106px);
  margin-top: clamp(40px, 9.8dvh, 106px);
}
.fhp-carousel {
  --fhp-per: 4;
  --fhp-arrow: 32px;
  --fhp-arrow-gap: 60px;
  --fhp-rail: min(
    calc(100cqw - 2 * (var(--fhp-arrow) + var(--fhp-arrow-gap))),
    calc(var(--fhp-per) * 208px + (var(--fhp-per) - 1) * 120px)
  );
  --fhp-card: min(208px, calc((var(--fhp-rail) - (var(--fhp-per) - 1) * 16px) / var(--fhp-per)));
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: var(--fhp-arrow-gap);
}
.fhp-viewport { flex: 0 0 auto; width: var(--fhp-rail); min-width: 0; }
.fhp-track {
  position: relative;
  display: flex;
  gap: calc((var(--fhp-rail) - var(--fhp-per) * var(--fhp-card)) / (var(--fhp-per) - 1));
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
}
.fhp-track::-webkit-scrollbar { display: none; }
.fhp-item { flex: 0 0 var(--fhp-card); min-width: 0; scroll-snap-align: start; text-align: left; }
/* Fewer episodes than fit: centre them (auto margins collapse once the
   rail overflows, so a full rail still scrolls from its first card). */
.fhp-item:first-child { margin-left: auto; }
.fhp-item:last-child { margin-right: auto; }
.fhp-card { display: block; color: inherit; text-decoration: none; outline: none; }
.fhp-cover {
  position: relative;
  display: block;
  box-sizing: border-box;
  aspect-ratio: 208 / 218;
  overflow: hidden;
  background: linear-gradient(145deg, #27314f 0%, #151b2c 100%);
  border: 1px solid #E4E8EF;
  border-radius: 14px;
}
.fhp-cover img { transition: transform 400ms ease; }
a.fhp-card:hover .fhp-cover img { transform: scale(1.04); }
.fhp-card:focus-visible .fhp-cover { outline: 2px solid #ffffff; outline-offset: -3px; }
.fhp-card-title, .fhp-card-desc {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.fhp-card-title { -webkit-line-clamp: 2; }
.fhp-card-desc { -webkit-line-clamp: 3; }
.fhp-arrow {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: var(--fhp-arrow);
  height: var(--fhp-arrow);
  margin-top: calc(var(--fhp-card) * 109 / 208 - var(--fhp-arrow) / 2);
  padding: 0;
  border: 1.5px solid #ffffff;
  border-radius: 999px;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  transition: background-color 150ms ease;
}
.fhp-arrow:hover { background-color: rgba(255, 255, 255, 0.12); }
.fhp-arrow:focus-visible,
.fhp-dot:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.85); outline-offset: 3px; }
.fhp-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: clamp(28px, 5.5vh, 59px);
  margin-top: clamp(28px, 5.5dvh, 59px);
}
.fhp-dot {
  position: relative;
  display: block;
  width: 5px;
  height: 5px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: #ECECEC 0% 0% no-repeat padding-box;
  cursor: pointer;
  transition: width 220ms ease, background-color 150ms ease;
}
/* A 5px dot is too small to hit; widen the target without moving the dots. */
.fhp-dot::before { content: ''; position: absolute; inset: -9px -3px; }
.fhp-dot[aria-current='true'] { width: 13px; background: #C1C1C1 0% 0% no-repeat padding-box; }
@container fhp (max-width: 1279px) {
  .fhp-carousel { --fhp-arrow-gap: 24px; }
}
@container fhp (max-width: 1039px) {
  .fhp-carousel { --fhp-per: 3; }
}
@container fhp (max-width: 779px) {
  .fhp-carousel { --fhp-per: 2; --fhp-arrow-gap: 16px; }
}
/* Phones swipe; the arrows would only eat the cards' width. */
@container fhp (max-width: 479px) {
  .fhp-carousel { --fhp-arrow: 0px; --fhp-arrow-gap: 0px; }
  .fhp-arrow { display: none; }
}
@media (max-width: 767px) {
  .fh-slider-frame.fhp-frame { padding-inline: 16px; }
  .fhp-title { font-size: 30px; }
  .fhp-eyebrow, .fhp-desc { font-size: 16px; line-height: 20px; }
  .fhp-desc { margin-top: 20px; }
  .fhp-platforms { flex-wrap: nowrap; gap: 10px; max-width: 416px; }
  /* Share the row by content so "Apple Podcasts" isn't the one cut short. */
  .fhp-platforms > li { flex: 1 1 auto; }
  .fhp-platform { padding: 0 6px; }
}
@media (prefers-reduced-motion: reduce) {
  .fhp-cover img, .fhp-dot, .fhp-platform { transition: none; }
}
`;

/** Keeps an author-typed family inside its declaration. */
const cleanFamily = (family?: string) => (family ?? '').replace(/[;{}<>]/g, '').trim();

/** Form fields can hand numbers over as strings ("80"). */
const clampNumber = (value: unknown, fallback: number, min: number, max: number) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
};

/**
 * One soft light: full strength at the core with a long faint tail,
 * sampled from the design. `size` and `strength` scale the whole falloff.
 */
const glowLayer = (
  color: string,
  x: number,
  y: number,
  width: number,
  height: number,
  size: number,
  strength: number,
) => {
  const mix = (k: number) =>
    `color-mix(in srgb, ${color} ${Math.round(k * strength * 10) / 10}%, transparent)`;
  const w = Math.round(width * size * 10) / 10;
  const h = Math.round(height * size * 10) / 10;
  return (
    `radial-gradient(${w}% ${h}% at ${x}% ${y}%, ${mix(100)} 0%, ${mix(78)} 30%, ` +
    `${mix(48)} 58%, ${mix(18)} 84%, transparent 100%)`
  );
};

const episodesOf = (episodes: unknown) =>
  asList<FullHeightPodcastEpisode>(episodes).filter(
    (e) => e && (tx(e.title) || tx(e.description) || e.image),
  );

/** Catalog course → episode card. The catalog has no episode model, so a
 *  podcast course's cover, runtime and blurb fill the card. */
const courseToEpisode = (item: LearningItem, hrefBase: string): FullHeightPodcastEpisode => ({
  title: item.title,
  description: item.description,
  image: item.imageUrl,
  duration: item.duration,
  href: item.href ?? courseDetailHref(item.title, item.id, hrefBase),
});

/** "open.spotify.com" — the best name a label-less custom logo has. */
const hostOf = (href?: string) => {
  try {
    return new URL(href ?? '').hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const PLATFORM_NAMES: Record<Exclude<FullHeightPodcastPlatformKind, 'custom'>, string> = {
  spotify: 'Spotify',
  apple: 'Apple Podcasts',
  'youtube-music': 'Music',
};

export function FullHeightPodcast({
  eyebrow,
  title,
  description,
  buttons: rawButtons,
  platforms: rawPlatforms,
  source = 'authored',
  pluginId = PODCAST_PLUGIN_ID,
  limit = 8,
  sortField = 'recent',
  hrefBase = COURSE_DETAIL_BASE,
  episodes: rawEpisodes,
  titleTag = 'h2',
  fontFamily = 'Inter',
  backgroundColor = '#0a0a0c',
  showGlow = true,
  glowColor = '#1c253e',
  glowX = 80,
  glowY = 36,
  showSecondaryGlow = true,
  secondaryGlowColor = '#1b253d',
  secondaryGlowX = 17,
  secondaryGlowY = 95,
  glowIntensity = 100,
  glowSize = 100,
}: FullHeightPodcastProps) {
  const currentUser = useCurrentUser<CurrentUser>();
  const part = useBlockPart();

  const fromCatalog = source === 'catalog';
  const courses = usePodcastCourses({ enabled: fromCatalog, pluginId, limit, sortField });
  const episodes = useMemo(
    () =>
      fromCatalog
        ? episodesOf(courses.map((item) => courseToEpisode(item, hrefBase)))
        : episodesOf(rawEpisodes).filter((e) => isVisible(e, currentUser)),
    [fromCatalog, courses, hrefBase, rawEpisodes, currentUser],
  );
  const buttons = asList<FullHeightPodcastButton>(rawButtons).filter((b) => b && tx(b.label));
  const platforms = asList<FullHeightPodcastPlatform>(rawPlatforms).filter(
    (p) => p && (tx(p.label) || (p.platform && p.platform !== 'custom') || p.logo),
  );

  const eyebrowText = tx(eyebrow);
  const titleText = tx(title);
  const descriptionText = tx(description);
  const hasContent = Boolean(
    eyebrowText ||
    titleText ||
    descriptionText ||
    buttons.length > 0 ||
    platforms.length > 0 ||
    episodes.length > 0,
  );
  const TitleTag = titleTag === 'h1' ? 'h1' : 'h2';

  const family = cleanFamily(fontFamily);
  const bodyFamily = family
    ? `${family}, var(--font-family-body, sans-serif)`
    : 'var(--font-family-body, inherit)';

  const size = clampNumber(glowSize, 100, 10, 300) / 100;
  const strength = clampNumber(glowIntensity, 100, 0, 100) / 100;
  // Sampled from the design: a navy light right of centre, a little above
  // the middle, and a wider one sunk into the bottom-left corner.
  const glowLayers = [
    showGlow
      ? glowLayer(
          glowColor,
          clampNumber(glowX, 80, -50, 150),
          clampNumber(glowY, 36, -50, 150),
          42,
          75,
          size,
          strength,
        )
      : null,
    showGlow && showSecondaryGlow
      ? glowLayer(
          secondaryGlowColor,
          clampNumber(secondaryGlowX, 17, -50, 150),
          clampNumber(secondaryGlowY, 95, -50, 150),
          45,
          90,
          size,
          strength,
        )
      : null,
  ].filter(Boolean);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: FULL_HEIGHT_PODCAST_CSS }} />
      <FullHeightSlider
        slides={hasContent ? [0] : []}
        background={backgroundColor}
        backdrop={
          glowLayers.length > 0 && strength > 0 ? (
            <div style={{ position: 'absolute', inset: 0, background: glowLayers.join(', ') }} />
          ) : null
        }
        renderSlide={(_, __, { hasControls }) => (
          <FullHeightFrame
            hasControls={hasControls}
            className="fhp-frame"
            style={{ alignItems: 'center' }}
          >
            <div className="fhp-inner" style={{ fontFamily: bodyFamily }}>
              {eyebrowText ? (
                <p {...part('kicker')} className="fhp-eyebrow" style={{ whiteSpace: 'pre-line' }}>
                  {eyebrowText}
                </p>
              ) : null}
              {titleText ? (
                <TitleTag
                  {...part('title')}
                  className="fhp-title"
                  style={{ fontFamily: 'inherit', whiteSpace: 'pre-line' }}
                >
                  {titleText}
                </TitleTag>
              ) : null}
              {descriptionText ? (
                <p {...part('subtitle')} className="fhp-desc" style={{ whiteSpace: 'pre-line' }}>
                  {descriptionText}
                </p>
              ) : null}
              {buttons.length > 0 ? (
                <div className="fhp-actions">
                  {buttons.map((button, i) => (
                    <Button
                      key={i}
                      {...part(i === 0 ? 'cta' : 'cta-secondary', i)}
                      href={button.href || '#'}
                      variant="ghost"
                      radius="pill"
                      className="fhp-btn"
                      {...(button.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      style={{
                        // Layout doesn't lean on the Button's utility classes.
                        display: 'inline-flex',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        height: 38,
                        padding: '0 25px',
                        border: '1px solid #ffffff',
                        color: '#ffffff',
                        fontFamily: 'inherit',
                        fontSize: 13,
                        fontWeight: 500,
                        textDecoration: 'none',
                      }}
                    >
                      {tx(button.label)}
                    </Button>
                  ))}
                </div>
              ) : null}
              {platforms.length > 0 ? (
                <ul {...part('platform-list')} className="fhp-platforms">
                  {platforms.map((platform, i) => (
                    <li key={i} {...part('platform', i)}>
                      <PlatformBadge platform={platform} />
                    </li>
                  ))}
                </ul>
              ) : null}
              {episodes.length > 0 ? <EpisodeRail episodes={episodes} /> : null}
            </div>
          </FullHeightFrame>
        )}
      />
    </>
  );
}

// =============================================================================
// Listening-platform badges
// =============================================================================

function PlatformBadge({ platform }: { platform: FullHeightPodcastPlatform }) {
  const kind = platform.platform ?? 'custom';
  const custom = kind === 'custom';
  const name = tx(platform.label) || (custom ? '' : PLATFORM_NAMES[kind]);

  let mark: ReactNode;
  if (kind === 'spotify') {
    mark = <Mark icon={<SpotifyIcon />} text={name} style={spotifyText} />;
  } else if (kind === 'apple') {
    mark = <Mark icon={<ApplePodcastsIcon />} text={name} style={appleText} />;
  } else if (kind === 'youtube-music') {
    // The wordmark reads "Music"; the full name stays available to readers.
    mark = (
      <Mark
        icon={<YouTubeMusicIcon />}
        text={name}
        style={youTubeMusicText}
        srLabel={tx(platform.label) ? undefined : 'YouTube Music'}
      />
    );
  } else {
    mark = (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        {platform.logo ? (
          <img
            src={platform.logo}
            // A label beside the logo already names the link.
            alt={name ? '' : hostOf(platform.href)}
            loading="lazy"
            decoding="async"
            style={{
              display: 'block',
              maxWidth: name ? 28 : 104,
              maxHeight: 24,
              objectFit: 'contain',
            }}
          />
        ) : null}
        {name ? <span style={appleText}>{name}</span> : null}
      </span>
    );
  }

  if (!platform.href) return <div className="fhp-platform">{mark}</div>;
  return (
    <PortalLink
      href={platform.href}
      className="fhp-platform"
      {...(platform.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {mark}
    </PortalLink>
  );
}

const spotifyText: CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: '#1ED760',
};
const appleText: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: '#1D1D1F',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const youTubeMusicText: CSSProperties = {
  fontSize: 17,
  fontWeight: 700,
  letterSpacing: '-0.03em',
  color: '#030303',
};

function Mark({
  icon,
  text,
  style,
  srLabel,
}: {
  icon: ReactNode;
  text: string;
  style: CSSProperties;
  srLabel?: string;
}) {
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0, lineHeight: 1 }}
    >
      {icon}
      <span aria-hidden={srLabel ? true : undefined} style={style}>
        {text}
      </span>
      {srLabel ? <span style={srOnly}>{srLabel}</span> : null}
    </span>
  );
}

const srOnly: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
};

function SpotifyIcon() {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" style={{ flex: 'none' }}>
      <circle cx="12" cy="12" r="12" fill="#1ED760" />
      <path
        d="M17.2 10.6c-2.9-1.7-7.6-1.9-10.3-1M16.4 13.3c-2.4-1.4-6.1-1.8-8.8-1M15.6 15.8c-2-1.2-4.9-1.4-7.2-.8"
        fill="none"
        stroke="#ffffff"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ApplePodcastsIcon() {
  return (
    <svg aria-hidden="true" width="19" height="19" viewBox="0 0 24 24" style={{ flex: 'none' }}>
      <defs>
        <linearGradient id="fhp-apple-podcasts" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F452FF" />
          <stop offset="1" stopColor="#832BC1" />
        </linearGradient>
      </defs>
      <rect width="24" height="24" rx="5.4" fill="url(#fhp-apple-podcasts)" />
      <path
        d="M7.9 16.2a6.3 6.3 0 1 1 8.2 0M9.7 14.2a3.6 3.6 0 1 1 4.6 0"
        fill="none"
        stroke="#ffffff"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <circle cx="12" cy="10.9" r="1.5" fill="#ffffff" />
      <rect x="11" y="13.2" width="2" height="5.6" rx="1" fill="#ffffff" />
    </svg>
  );
}

function YouTubeMusicIcon() {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" style={{ flex: 'none' }}>
      <circle cx="12" cy="12" r="12" fill="#FF0000" />
      <circle cx="12" cy="12" r="6.1" fill="none" stroke="#ffffff" strokeWidth="1.2" />
      <path d="M10.2 9.1v5.8l4.9-2.9z" fill="#ffffff" />
    </svg>
  );
}

// =============================================================================
// The episode rail
// =============================================================================

function EpisodeRail({ episodes }: { episodes: FullHeightPodcastEpisode[] }) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const trackRef = useRef<HTMLUListElement>(null);
  const count = episodes.length;

  // The server can't know the viewport: assume the desktop's four cards and
  // let the first measurement correct it (only the dot count can change —
  // the card sizes are CSS).
  const [perView, setPerView] = useState(Math.min(4, count));
  const [rawActive, setActive] = useState(0);
  const stops = Math.max(1, count - perView + 1);
  const active = Math.min(rawActive, stops - 1);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const step = stepOf(track);
      const first = track.children[0] as HTMLElement | undefined;
      if (!first || step <= 0) return;
      const gap = step - first.offsetWidth;
      const per = Math.max(1, Math.min(count, Math.round((track.clientWidth + gap) / step)));
      const last = Math.max(0, count - per);
      // The last stop can be less than a whole step from the one before it.
      const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      setPerView(per);
      setActive(atEnd ? last : Math.min(last, Math.round(track.scrollLeft / step)));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    track.addEventListener('scroll', schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(track);
    return () => {
      track.removeEventListener('scroll', schedule);
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [count]);

  const go = (stop: number) => {
    const track = trackRef.current;
    if (!track || stops < 2) return;
    const target = ((stop % stops) + stops) % stops;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: target * stepOf(track), behavior: reduced ? 'auto' : 'smooth' });
    setActive(target);
  };

  const hasControls = stops > 1;

  return (
    <div className="fhp-slider">
      <div className="fhp-carousel">
        {hasControls ? (
          <button
            type="button"
            className="fhp-arrow"
            aria-label={t('hero.carousel.previous')}
            onClick={() => go(active - 1)}
            {...part('pager', 0)}
          >
            <Chevron direction="prev" />
          </button>
        ) : null}
        <div className="fhp-viewport">
          <ul ref={trackRef} {...part('list')} className="fhp-track">
            {episodes.map((episode, i) => (
              <li key={i} {...part('item', i)} className="fhp-item">
                <EpisodeCard episode={episode} />
              </li>
            ))}
          </ul>
        </div>
        {hasControls ? (
          <button
            type="button"
            className="fhp-arrow"
            aria-label={t('hero.carousel.next')}
            onClick={() => go(active + 1)}
            {...part('pager', 1)}
          >
            <Chevron direction="next" />
          </button>
        ) : null}
      </div>
      {hasControls ? (
        <div className="fhp-dots" role="group" aria-label={t('hero.carousel.selector')}>
          {Array.from({ length: stops }, (_, i) => (
            <button
              key={i}
              type="button"
              className="fhp-dot"
              aria-current={i === active ? 'true' : undefined}
              aria-label={t('hero.carousel.goToSlide', { index: i + 1 })}
              onClick={() => go(i)}
              {...part('tab', i)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Distance between two neighbouring cards' left edges (card + gap). */
function stepOf(track: HTMLElement): number {
  const [a, b] = [track.children[0], track.children[1]] as Array<HTMLElement | undefined>;
  if (!a) return 0;
  return b ? b.offsetLeft - a.offsetLeft : a.offsetWidth;
}

function EpisodeCard({ episode }: { episode: FullHeightPodcastEpisode }) {
  const cardTitle = tx(episode.title);
  const cardDescription = tx(episode.description);
  const duration = tx(episode.duration);
  const badge = tx(episode.badge);
  const alt = episode.imageAlt?.trim() ?? '';

  const inner = (
    <>
      <span className="fhp-cover">
        {episode.image ? (
          <img
            src={resizeImageUrl(episode.image, '480x0')}
            alt={alt}
            aria-hidden={alt ? undefined : true}
            loading="lazy"
            decoding="async"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255, 255, 255, 0.35)',
            }}
          >
            <HeadphonesIcon />
          </span>
        )}
        {badge ? (
          <span
            style={{
              position: 'absolute',
              top: 11,
              right: 15,
              display: 'inline-flex',
              alignItems: 'center',
              height: 16,
              padding: '0 14px',
              borderRadius: 999,
              background: '#E53326',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1,
              color: '#ffffff',
              whiteSpace: 'nowrap',
            }}
          >
            {badge}
          </span>
        ) : null}
        {duration ? (
          <span
            style={{
              position: 'absolute',
              left: 10,
              bottom: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 26,
              padding: '0 12px 0 11px',
              borderRadius: 999,
              background: '#ffffff',
              fontSize: 10,
              fontWeight: 500,
              lineHeight: 1,
              color: '#A7A7A7',
              whiteSpace: 'nowrap',
            }}
          >
            <svg aria-hidden="true" width="7" height="8" viewBox="0 0 7 8" fill="currentColor">
              <path d="M0 0.6v6.8a.5.5 0 0 0 .76.43l5.7-3.4a.5.5 0 0 0 0-.86L.76.17A.5.5 0 0 0 0 .6z" />
            </svg>
            {duration}
          </span>
        ) : null}
      </span>
      {cardTitle ? (
        <span
          className="fhp-card-title"
          style={{
            marginTop: 23,
            fontSize: 15,
            fontWeight: 400,
            lineHeight: '18px',
            color: '#ffffff',
          }}
        >
          {cardTitle}
        </span>
      ) : null}
      {cardDescription ? (
        <span
          className="fhp-card-desc"
          style={{
            marginTop: cardTitle ? 9 : 23,
            fontSize: 10,
            fontWeight: 400,
            lineHeight: '12px',
            color: '#B1B1B1',
          }}
        >
          {cardDescription}
        </span>
      ) : null}
    </>
  );

  if (!episode.href) return <div className="fhp-card">{inner}</div>;
  return (
    <PortalLink
      href={episode.href}
      className="fhp-card"
      {...(episode.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {inner}
    </PortalLink>
  );
}

function Chevron({ direction }: { direction: 'prev' | 'next' }) {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {direction === 'prev' ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  );
}

function HeadphonesIcon() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  );
}
